import React from 'react';
import { Link } from 'react-router-dom';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

const WrongfulDeath = () => {
  return (
    <div data-testid="wrongful-death-page">
      {/* Hero */}
      <section className="py-10 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <h1 className="text-3xl sm:text-5xl md:text-6xl font-heading font-bold text-slate-900 mb-6 tracking-tight">
            Wrongful Death
          </h1>
          <p className="text-base md:text-xl text-slate-600 leading-relaxed">
            Losing a loved one due to someone else's negligence is devastating. While nothing can replace your loss, 
            pursuing a wrongful death claim can provide financial security and hold the responsible party accountable. 
            We handle these sensitive cases with compassion and thoroughness.
          </p>
        </div>
      </section>

      {/* How We Help */}
      <section className="py-10 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <h2 className="text-2xl md:text-4xl font-heading font-semibold text-slate-900 mb-8 tracking-tight">
            How We Help
          </h2>
          <div className="space-y-4 text-slate-600 leading-relaxed">
            <p className="flex items-center gap-3">
              <span className="text-primary font-bold leading-none">•</span>
              <span>Compassionate guidance through the legal process during an unimaginably difficult time</span>
            </p>
            <p className="flex items-center gap-3">
              <span className="text-primary font-bold leading-none">•</span>
              <span>Thorough investigation to determine liability and preserve critical evidence</span>
            </p>
            <p className="flex items-center gap-3">
              <span className="text-primary font-bold leading-none">•</span>
              <span>Identification of all available sources of compensation</span>
            </p>
            <p className="flex items-center gap-3">
              <span className="text-primary font-bold leading-none">•</span>
              <span>Documentation of economic and non-economic damages to surviving family members</span>
            </p>
            <p className="flex items-center gap-3">
              <span className="text-primary font-bold leading-none">•</span>
              <span>Aggressive representation against insurance companies and defense attorneys</span>
            </p>
            <p className="flex items-center gap-3">
              <span className="text-primary font-bold leading-none">•</span>
              <span>Experience handling wrongful death claims arising from auto accidents, medical malpractice, workplace incidents, and other negligent conduct</span>
            </p>
          </div>
        </div>
      </section>

      {/* Who Can File */}
      <section className="py-10 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <h2 className="text-2xl md:text-4xl font-heading font-semibold text-slate-900 mb-8 tracking-tight">
            Who Can File a Wrongful Death Claim?
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed mb-6">
            Both North Carolina and South Carolina have specific rules about who may bring a wrongful death action:
          </p>
          <div className="space-y-6">
            <div className="bg-white border border-slate-200 p-6">
              <h3 className="text-xl font-heading font-semibold text-primary mb-3">
                North Carolina
              </h3>
              <p className="text-slate-600 leading-relaxed">
                The personal representative (executor or administrator) of the deceased's estate must file the claim. 
                Damages are distributed to surviving family members according to the NC Intestate Succession Act.
              </p>
            </div>
            <div className="bg-white border border-slate-200 p-6">
              <h3 className="text-xl font-heading font-semibold text-primary mb-3">
                South Carolina
              </h3>
              <p className="text-slate-600 leading-relaxed">
                The personal representative files the claim on behalf of statutory beneficiaries (spouse, children, 
                parents, or heirs at law).
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Damages */}
      <section className="py-10 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <h2 className="text-2xl md:text-4xl font-heading font-semibold text-slate-900 mb-8 tracking-tight">
            Recoverable Damages
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed mb-6">
            Wrongful death claims may include compensation for:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border-l-4 border-primary pl-4">
              <p className="font-semibold text-slate-900 mb-1">Medical and Funeral Expenses</p>
              <p className="text-sm text-slate-600">Costs incurred before death and burial costs</p>
            </div>
            <div className="border-l-4 border-primary pl-4">
              <p className="font-semibold text-slate-900 mb-1">Lost Income and Support</p>
              <p className="text-sm text-slate-600">Economic contributions the deceased would have provided</p>
            </div>
            <div className="border-l-4 border-primary pl-4">
              <p className="font-semibold text-slate-900 mb-1">Loss of Companionship</p>
              <p className="text-sm text-slate-600">The value of the relationship and guidance</p>
            </div>
            <div className="border-l-4 border-primary pl-4">
              <p className="font-semibold text-slate-900 mb-1">Pain and Suffering</p>
              <p className="text-sm text-slate-600">If the deceased experienced conscious pain before death</p>
            </div>
            <div className="border-l-4 border-primary pl-4">
              <p className="font-semibold text-slate-900 mb-1">Punitive Damages</p>
              <p className="text-sm text-slate-600">In cases of gross negligence or willful conduct</p>
            </div>
          </div>
        </div>
      </section>

      {/* What to Do Next */}
      <section className="py-10 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <h2 className="text-2xl md:text-4xl font-heading font-semibold text-slate-900 mb-8 tracking-tight">
            Steps to Take
          </h2>
          <div className="space-y-6">
            <div className="border-l-4 border-primary pl-6">
              <h3 className="text-xl font-heading font-semibold text-slate-900 mb-2">
                Preserve Evidence
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Do not discard clothing, documents, or other evidence related to the incident. These items may be crucial to your case.
              </p>
            </div>
            <div className="border-l-4 border-primary pl-6">
              <h3 className="text-xl font-heading font-semibold text-slate-900 mb-2">
                Avoid Early Settlements
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Insurance companies may offer quick settlements that do not reflect the true value of your loss. 
                Consult with us before accepting any offer or signing any documents.
              </p>
            </div>
            <div className="border-l-4 border-primary pl-6">
              <h3 className="text-xl font-heading font-semibold text-slate-900 mb-2">
                Contact an Attorney Promptly
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Wrongful death claims are subject to strict time limits. The sooner you contact us, the sooner we can 
                begin protecting your family's rights and preserving evidence.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Service Area */}
      <section className="py-12 bg-primary text-white">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl text-center">
          <p className="text-lg">
            Representing families throughout <strong>North Carolina</strong> and <strong>South Carolina</strong>.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-10 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <h2 className="text-2xl md:text-4xl font-heading font-semibold text-slate-900 mb-8 tracking-tight">
            Frequently Asked Questions
          </h2>
          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="item-1" className="border border-slate-200 px-4 md:px-6">
              <AccordionTrigger className="text-left font-heading text-lg py-4" data-testid="faq-1">
                How long do I have to file a wrongful death claim?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed pb-4">
                Generally, wrongful death claims must be filed within two years of the date of death in North Carolina 
                and three years in South Carolina. However, certain exceptions may apply. Contact us immediately to 
                ensure your rights are protected.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="border border-slate-200 px-4 md:px-6">
              <AccordionTrigger className="text-left font-heading text-lg py-4" data-testid="faq-2">
                What is the difference between wrongful death and a criminal case?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed pb-4">
                Criminal cases are brought by the government to punish wrongdoing. Wrongful death cases are civil lawsuits 
                brought by the family to recover financial compensation. Both may proceed separately. A criminal conviction 
                is not required to prevail in a civil wrongful death claim.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="border border-slate-200 px-4 md:px-6">
              <AccordionTrigger className="text-left font-heading text-lg py-4" data-testid="faq-3">
                Can I file a wrongful death claim if my loved one was partially at fault?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed pb-4">
                It depends on the state and degree of fault. North Carolina's contributory negligence rule is strict, 
                while South Carolina's comparative fault system allows recovery if the deceased was not more than 50% at fault. 
                The specific facts matter greatly. We evaluate each case carefully.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4" className="border border-slate-200 px-4 md:px-6">
              <AccordionTrigger className="text-left font-heading text-lg py-4" data-testid="faq-4">
                How is compensation distributed among family members?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed pb-4">
                Distribution depends on state law and whether the deceased had a will. In general, surviving spouses and 
                children are the primary beneficiaries. We guide families through the distribution process.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-semibold text-slate-900 mb-6 tracking-tight">
            We're Here for Your Family
          </h2>
          <p className="text-lg text-slate-600 mb-8 leading-relaxed">
            Contact us for a compassionate, confidential consultation about your wrongful death case.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-primary text-white px-8 py-4 rounded-sm font-semibold tracking-wider uppercase text-sm shadow-sm hover:bg-primary/90 transition-colors"
              data-testid="cta-consultation"
            >
              Request a Consultation
            </Link>
            <a
              href="tel:+13363541040"
              className="border-2 border-primary text-primary px-8 py-4 rounded-sm font-semibold tracking-wider uppercase text-sm hover:bg-primary/10 transition-colors"
              data-testid="cta-call"
            >
              Call 336-354-1040
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WrongfulDeath;